import { useContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Drawer, Button, Stack, Text, LoadingOverlay, Card } from '@mantine/core';

import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';

import { useMenuManagement } from 'hooks/useMenuManagement';
import { patchMerchantMenuOrdinals } from 'helpers/menuManagement';
import {
  useHandleSuccessNotification,
  useHandleErrorNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';

import { SORT_TYPE_LABEL_PLURAL_OVERRIDES } from 'components/MenuManagement/MenuEdit/MenuDragSort/constants';
import {
  TYPE_TO_READABLE_STRING_PLURAL,
  TYPE_TO_READABLE_STRING_MAP,
  MENU_OBJECT_TYPES,
} from 'constants/menuManagement';

import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import SortableElement from 'components/MenuManagement/MenuEdit/MenuDragSort/SortableElement';

import { Menu, MenuElement } from 'components/MenuManagement/MenuEdit/MenuDragSort/types';

export interface MenuDragSortDrawerProps {
  opened: boolean;
  onClose: () => void;
  type: keyof typeof TYPE_TO_READABLE_STRING_PLURAL;
  sortableElements: MenuElement[];
  fetchError?: string;
}

function hasFailedArray(response: unknown): response is { failed: unknown[] } {
  return (
    typeof response === 'object' &&
    response !== null &&
    'failed' in response &&
    Array.isArray(response.failed)
  );
}

export default function MenuDragSortDrawer({
  opened,
  onClose,
  type,
  sortableElements = [],
  fetchError,
}: MenuDragSortDrawerProps) {
  const { menu } = useContext(MenuEdit.Context) as { menu: Menu };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { getSingleCubohMenu } = useMenuManagement();
  const [orderedElements, setOrderedElements] = useState(sortableElements);
  const [isSaving, setIsSaving] = useState(false);
  const handleSuccessNotification = useHandleSuccessNotification();
  const handleErrorNotification = useHandleErrorNotification();

  const parentObj: { name?: string } | undefined = sortableElements[0]?.parents?.[0];

  // reset the sort order when drawer opens/closes without saving
  useEffect(() => {
    if (opened) {
      setOrderedElements(sortableElements);
    }
  }, [opened, sortableElements]);

  const getDrawerTitle = (
    sortType: keyof typeof TYPE_TO_READABLE_STRING_PLURAL,
    parent: { name?: string } | null,
  ): string => {
    const pluralLabel =
      (sortType in SORT_TYPE_LABEL_PLURAL_OVERRIDES
        ? SORT_TYPE_LABEL_PLURAL_OVERRIDES[sortType]
        : TYPE_TO_READABLE_STRING_PLURAL[sortType]) || sortType;
    const contextName = parent?.name || menu.name;
    return `Sort ${pluralLabel} in ${contextName}`;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = orderedElements.findIndex(el => el.uuid === active.id);
      const newIndex = orderedElements.findIndex(el => el.uuid === over.id);
      setOrderedElements(arrayMove(orderedElements, oldIndex, newIndex));
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const parentName = parentObj?.name || menu.name;

      const items = orderedElements.map((element, index) => ({
        uuid: element.uuid,
        parentUuid: element.parentUuid,
        ordinal: index,
        itemType: element.itemType,
      }));

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = await patchMerchantMenuOrdinals({
        companyID: menu.companyId,
        merchantID: menu.merchantId,
        items,
      });

      // Check for 207 partial success
      // usually happens when a menu is being edited by multiple users
      // and we try to sort a deleted item
      if (hasFailedArray(response)) {
        const failedCount = response.failed.length;
        const label =
          (failedCount === 1
            ? TYPE_TO_READABLE_STRING_MAP[type]
            : TYPE_TO_READABLE_STRING_PLURAL[type]) || type;

        handleErrorNotification({
          customMessage: `We could not sort ${failedCount} ${label} in "${parentName}"`,
        });
      }

      setIsSaving(false);

      if (type === MENU_OBJECT_TYPES.categories) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        await getSingleCubohMenu(menu.companyId, menu.merchantId, menu.uuid);
      }

      handleSuccessNotification({
        parentUuid: sortableElements[0]?.parentUuid || menu.uuid,
        customMessage: `Successfully sorted ${TYPE_TO_READABLE_STRING_PLURAL[type] || type} in "${parentName}"`,
      });
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        error.name = '[MENU MANAGEMENT] Failed to sort menu items';
        Sentry.captureException(error, {
          extra: {
            merchant_id: menu.merchantId,
            company_id: menu.companyId,
            error_message: error.message,
          },
        });
      } else {
        Sentry.captureException(error);
      }
      setIsSaving(false);

      handleErrorNotification({
        customMessage: 'We could not update your menu. Please contact customer support.',
      });
      onClose();
    }
  };

  const dockButtons = (
    <>
      <Button variant="outline" onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button onClick={() => void handleSave()} loading={isSaving}>
        Save
      </Button>
    </>
  );

  let content;
  if (isSaving) {
    content = <LoadingOverlay visible />;
  } else if (fetchError) {
    content = (
      <Card bg="var(--surface-secondary)" p="xl">
        <Text ta="center" variant="sublabel-sm">
          {fetchError}
        </Text>
      </Card>
    );
  } else {
    content = (
      <>
        <Text variant="sublabel-sm" mb="md">
          Drag the cards below to reorder your menu.
        </Text>
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={orderedElements.map(el => el.uuid)}
            strategy={verticalListSortingStrategy}
          >
            <Stack gap="sm">
              {orderedElements.map((element, index) => (
                <SortableElement key={element.uuid} element={element} index={index} />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      </>
    );
  }

  return (
    <Drawer
      title={getDrawerTitle(type, parentObj || null)}
      opened={opened}
      onClose={onClose}
      variant="withDock"
    >
      <DockedDrawerFormContent dockButtons={dockButtons}>{content}</DockedDrawerFormContent>
    </Drawer>
  );
}
